import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Calendar, Clock, ArrowRight, Headphones, Rss } from "lucide-react";
import { Reveal, SectionTitle } from "../components/common";
import { publicApi, API } from "../lib/api";
import PodcastSeriesList from "../components/PodcastSeriesList";
import VideoModal, { VideoThumb } from "../components/VideoModal";
import PodcastSubscribeForm from "../components/PodcastSubscribeForm";
import EpisodeDescription from "../components/EpisodeDescription";
import Seo from "../components/Seo";

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function Podcast() {
  const [podcasts, setPodcasts] = useState([]);
  const [active, setActive] = useState(null);
  const [settings, setSettings] = useState({});
  const [seriesMap, setSeriesMap] = useState({});
  useEffect(() => {
    publicApi.podcasts().then(setPodcasts).catch(() => {});
    publicApi.settings().then(setSettings).catch(() => {});
    publicApi.series().then((list) => setSeriesMap(Object.fromEntries((list || []).map((s) => [s.slug, s.image])))).catch(() => {});
  }, []);

  const platforms = [
    { key: "podcast_spotify", label: "Spotify", brand: "#1DB954" },
    { key: "podcast_apple", label: "Apple Podcasts", brand: "#8940FA" },
    { key: "podcast_youtube", label: "YouTube", brand: "#FF0000" },
  ].filter((p) => settings[p.key]);

  return (
    <div>
      <Seo title="The Podcast — More Than Numbers" description="Real conversations about business, tax and cloud accounting — the CASS podcast, giving you more than numbers, one episode at a time." />
      {active && <VideoModal videoId={active.youtubeId} title={active.title} onClose={() => setActive(null)} />}
      <section className="numbers-pattern text-white">
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24">
          <span className="text-orange font-bold text-sm tracking-widest uppercase">More Than Numbers</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl uppercase leading-[0.95] mt-3 flex items-center gap-4">The Podcast <Headphones className="text-orange hidden md:block" size={48} /></h1>
          <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed">Real conversations about business, tax and cloud accounting — giving you more than numbers, one episode at a time.</p>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal><SectionTitle eyebrow={podcasts.length > 1 ? "Latest episodes" : "Latest episode"} title="Listen & learn" /></Reveal>
          <div className="space-y-6">
            {podcasts.map((p, i) => (
              <Reveal key={p.id} delay={i * 80}>
                <button type="button" data-testid={`podcast-card-${p.id}`} onClick={() => setActive(p)} className="w-full text-left group grid md:grid-cols-[280px_1fr] gap-6 bg-slate-50 rounded-2xl overflow-hidden border border-slate-100 hover:shadow-lg transition-all">
                  <div className="relative aspect-video overflow-hidden bg-navy self-center">
                    <VideoThumb src={p.image} youtubeId={p.youtubeId} fallback={seriesMap[p.series]} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-navy/40 flex items-center justify-center">
                      <span className="w-14 h-14 rounded-full bg-orange flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl"><Play size={22} className="text-white ml-1" fill="white" /></span>
                    </div>
                  </div>
                  <div className="p-6 md:py-8 md:pr-8">
                    <span className="inline-block bg-orange/10 text-orange text-xs font-bold px-3 py-1 rounded-full mb-3">{p.episode}</span>
                    <h3 className="font-display font-bold text-xl md:text-2xl text-navy leading-snug mb-3 group-hover:text-orange transition-colors">{p.title}</h3>
                    <EpisodeDescription text={p.description} className="text-slate-500 leading-relaxed mb-4" />
                    <div className="flex items-center gap-5 text-xs text-slate-400">
                      <span className="flex items-center gap-1.5"><Calendar size={13} /> {formatDate(p.date)}</span>
                      <span className="flex items-center gap-1.5"><Clock size={13} /> {p.duration}</span>
                    </div>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/podcast/all" data-testid="view-all-episodes-link" className="inline-flex items-center gap-2 border-2 border-navy hover:bg-navy hover:text-white text-navy font-bold px-7 py-3 rounded-full transition-all">
              View all episodes <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <PodcastSeriesList />


      <section className="py-16 bg-navy text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl mb-4">Never miss an episode</h2>
          <p className="text-slate-300 mb-8">Subscribe on your favourite platform and get more than numbers delivered to your ears.</p>
          {platforms.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-3 mb-8" data-testid="subscribe-links">
              {platforms.map((p) => (
                <a
                  key={p.key}
                  data-testid={`subscribe-${p.label.toLowerCase().split(" ")[0]}`}
                  href={settings[p.key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-full text-white transition-all hover:-translate-y-0.5 shadow-lg"
                  style={{ backgroundColor: p.brand }}
                >
                  {p.label}
                </a>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5">Let's Talk <ArrowRight size={18} /></Link>
            <a data-testid="podcast-rss-link" href={`${API}/podcast/rss.xml`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-white/20 hover:border-orange hover:text-orange text-slate-300 font-bold px-6 py-4 rounded-full transition-all"><Rss size={18} /> RSS feed</a>
          </div>

          <div className="mt-12 pt-10 border-t border-white/10">
            <p className="text-slate-300 mb-5 font-semibold">Prefer email? Get new episodes straight to your inbox.</p>
            <PodcastSubscribeForm />
          </div>
        </div>
      </section>
    </div>
  );
}
